// The three checks on the "Testing connection" screen, as pure decisions.
//
// Extracted from the screen because this is where Didier's ENG-3796 finding
// lives, and inside a 900-line component none of it was reachable from a unit
// test. Every rule below is one an assertion can hold.
//
// THE DEFECT, precisely. `bridge/encoder/.../callback/device/on_add.go` seeds a
// newly added device with movement status CONTROL_STATE_OK — in a block whose
// own comment says "with unset values" — before any control command has been
// attempted. The proto defines CONTROL_STATE_UNKNOWN = 0 for exactly that
// state. The screen then passed the control check on:
//
//     canonicalControlStatus !== CONNECTION_STATUS_FAILED &&
//     controlStatus === MovementStatus.CONTROL_STATE_OK
//
// For a camera with no control, no PTZ, or an unreachable one, the canonical
// status is never reported, so it is undefined, `undefined !== FAILED` is true,
// and the seeded OK closes the gate. The same disjunct also appeared in the
// "camera answered" test, so it could tick the REACHABLE row too — on a screen
// whose own comment says that row "may only tick on POSITIVE evidence".
//
// The rule here: the canonical `status.movement.connectionStatus.status` is
// authoritative (per `state-sync-status-field-chain-movement-connectionstatus`),
// and silence is not success. `movement.status` is read for CONTROL_STATE_ERROR
// only. That asymmetry is deliberate, not an oversight: the seeded value is OK
// and never ERROR, so a negative reading cannot be poisoned by the seed, and a
// signal that can only ever fail a check is safe to trust.
//
// Per rules: ai-code-requires-tests, code-comments-required,
// reference-rules-in-comments (ai.knowledge/global).

import { StatusDeviceConnectionStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { Status as MovementStatus } from '@studio/models/control/movement';

/** Render state of one check row. */
export type CheckStatus = 'pending' | 'active' | 'done' | 'error';

/** Where the overall add attempt has got to. */
export type AttemptStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'blocked';

/**
 * Does the control row run at all?
 *
 * Two reasons it does not, and neither renders a row: the user said the camera
 * has no control, and the camera reports nothing to control. In both cases the
 * row is ABSENT rather than ticked or crossed, because nothing was tested and
 * claiming either outcome would be a statement about a check that never ran.
 *
 * `userWantsControl` is the answer the user gave on the Connect controls step.
 * It is deliberately not derived from "did a control URI parse": Didier's logs
 * show `ptzcontroller://…:2000` present on cameras he had explicitly answered
 * "no control" for, so the URI reads true and the step ran anyway.
 */
export const isControlChecked = (input: {
  readonly userWantsControl: boolean | undefined;
  readonly hasMovementCapability: boolean | undefined;
}): boolean => {
  if (input.userWantsControl === false) return false;
  if (input.hasMovementCapability === false) return false;

  return true;
};

/** Everything the control row reads. */
export interface ControlSignals {
  /** False when the row is not rendered — see {@link isControlChecked}. */
  readonly checked: boolean;
  readonly attempt: AttemptStatus;
  /** `status.movement.connectionStatus.status`. The authoritative field. */
  readonly canonical: StatusDeviceConnectionStatus | undefined;
  /** `status.movement.status`. Read for ERROR only; its OK is seeded. */
  readonly movement: MovementStatus | undefined;
  /** An e2e probe observed a real control round-trip. */
  readonly observedControlConnected?: boolean | undefined;
  /** The bounded wait for a canonical answer has expired. */
  readonly timedOut?: boolean | undefined;
}

/**
 * Resolve the "Camera control responds" row.
 *
 * A terminal failure settles this row as an error rather than leaving it
 * spinning: the attempt is over and control was never established, and a row
 * that reads "still checking" forever beside two settled rows invites the user
 * to wait for something that will never arrive.
 */
export const resolveControlCheck = (signals: ControlSignals): CheckStatus => {
  if (!signals.checked) return 'pending';
  // Nothing was attempted, so there is no verdict to give.
  if (signals.attempt === 'blocked') return 'pending';
  if (signals.attempt === 'error') return 'error';
  if (signals.attempt !== 'connected') return 'pending';

  if (
    signals.observedControlConnected === true ||
    signals.canonical === StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED
  ) {
    return 'done';
  }

  // A FAILED status is NOT terminal until the wait is over.
  //
  // The bridge publishes CONNECTION_STATUS_FAILED first and
  // CONNECTION_STATUS_CONNECTED a moment later while VISCA establishes —
  // measured on 192.168.1.221, 2026-08-28: `canonicalControlStatus: 2` then
  // `3`, seconds apart, on a camera whose controls work. Latching on the first
  // value crossed the row out and pressing Re-test turned it green, which is
  // the signature of reading a transient as a verdict.
  //
  // So a failure only counts once the ceiling expires. The end state is
  // unchanged — still FAILED at CONTROL_CHECK_TIMEOUT_MS is still an error —
  // but a camera that connects a beat late is no longer reported as broken.
  if (signals.timedOut !== true) return 'active';

  // The ceiling expired without a CONNECTED. Whether the bridge said FAILED or
  // said nothing at all, the answer to the user is the same.
  return 'error';
};

/**
 * Everything the reachable row reads.
 *
 * `movement` is deliberately ABSENT. The shipped code read the seeded
 * CONTROL_STATE_OK here as well as in the control row, so a closed port could
 * render "Reachable on network ✓". Leaving the field off the type means the
 * compiler refuses the mistake rather than a test having to catch it.
 */
export interface ReachSignals {
  readonly attempt: AttemptStatus;
  /** A frame really decoded. */
  readonly videoReady: boolean;
  readonly canonical: StatusDeviceConnectionStatus | undefined;
  readonly observedControlConnected?: boolean | undefined;
  /** The camera itself answered 401. A refusal is still an answer. */
  readonly authRefused?: boolean | undefined;
  /** The bounded wait for a frame has expired. */
  readonly streamTimedOut?: boolean | undefined;
}

/**
 * Did something actually answer at that address?
 *
 * Only positive evidence counts. The depot resolving the device is NOT
 * evidence — it means the bridge accepted the config — which is why a port with
 * nothing listening on it once rendered "Reachable on network ✓". A decoded
 * frame, a real control round-trip, and a refusal from the camera itself are
 * each proof that a host is there. The seeded movement OK is not, which is why
 * it is absent from this list.
 */
export const cameraAnswered = (signals: ReachSignals): boolean =>
  signals.videoReady ||
  signals.observedControlConnected === true ||
  signals.canonical === StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED ||
  signals.authRefused === true;

/** Resolve the "Reachable on network" row. */
export const resolveReachCheck = (signals: ReachSignals): CheckStatus => {
  const answered = cameraAnswered(signals);

  switch (signals.attempt) {
    // Refused before any bridge traffic: the camera was never contacted, so
    // crossing this row out would be a claim about a test that did not run.
    case 'blocked':
      return 'pending';
    case 'connecting':
      return 'active';
    case 'connected':
      if (answered) return 'done';
      return signals.streamTimedOut === true ? 'error' : 'active';
    case 'error':
      return answered ? 'done' : 'error';
    default:
      return 'pending';
  }
};

/** Everything the stream row reads. */
export interface StreamSignals {
  readonly attempt: AttemptStatus;
  readonly videoReady: boolean;
  readonly timedOut?: boolean | undefined;
}

/**
 * Resolve the "Video stream readable" row.
 *
 * Ticks on a decoded frame and nothing else. "The bridge resolved the config"
 * is not a readable stream.
 */
export const resolveStreamCheck = (signals: StreamSignals): CheckStatus => {
  if (signals.attempt === 'blocked') return 'pending';
  if (signals.attempt === 'error') return 'error';
  if (signals.attempt !== 'connected') return 'pending';
  if (signals.videoReady) return 'done';

  return signals.timedOut === true ? 'error' : 'active';
};

/**
 * May the funnel advance?
 *
 * Every rendered row must have passed. A row that is not rendered cannot block,
 * which is the whole point of {@link isControlChecked} — otherwise a camera with
 * no PTZ would wait forever on a check the user never sees.
 */
export const mayAdvance = (input: {
  readonly attempt: AttemptStatus;
  readonly reachCheck: CheckStatus;
  readonly streamCheck: CheckStatus;
  readonly controlChecked: boolean;
  readonly controlCheck: CheckStatus;
}): boolean =>
  input.attempt === 'connected' &&
  input.reachCheck === 'done' &&
  input.streamCheck === 'done' &&
  (!input.controlChecked || input.controlCheck === 'done');

/**
 * Is this camera ALREADY in the room?
 *
 * Onboarding must recognise its own work. Leaving the connection test while an
 * add is in flight lets that add finish anyway — there is no cancellation on
 * the add path — so coming back finds the camera present. Trying to add it a
 * second time then hits the plan source limit and tells a user midway through
 * ONBOARDING that they have too many cameras, about the one camera they were
 * being walked through adding.
 *
 * Matching on host: that is the field the shared `isDiscoveryInUse` keys on for
 * the RTSP family, and it is the one value both the form and the discovery
 * record always carry.
 */
export const findExistingCamera = (
  devices: readonly { readonly id: string; readonly host?: string | undefined }[],
  host: string
): string | undefined => {
  const wanted = host.trim();
  if (wanted === '') return undefined;

  return devices.find((device) => device.host?.trim() === wanted)?.id;
};

/**
 * Does the bridge's video error say the CAMERA refused our credentials?
 *
 * The transport is where the distinction lives: a wrong or missing password
 * answers `DESCRIBE failed: 401`, which the agent surfaces on the device's
 * `video.error`. Reading it is what lets the screen say "wrong credentials"
 * instead of "did not connect in time" — and a refusal is also positive proof
 * the camera IS there, which is why cameraAnswered() counts it.
 *
 * Deliberately narrow. 401/403 and the word unauthorized are refusals; a
 * timeout, a bad path, and a codec mismatch are not, and telling someone to
 * check their password when the address is wrong sends them the wrong way.
 */
export const isAuthRefusal = (videoError: string | undefined): boolean =>
  videoError !== undefined && /\b(401|403)\b|unauthor|not authorized|forbidden/i.test(videoError);
